/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { PositionReportSource } from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface PositionReportSummaryProps {
  id: string;
  positionTime: Date;
  source: PositionReportSource;
}

export class PositionReportSummaryRecord extends Record<PositionReportSummaryProps>({
  id: null as any,
  positionTime: null as any,
  source: null as any,
}) {
  public static fromJS(json: {
    id: string;
    positionTime: string | number | Date;
    source: PositionReportSource;
  }): PositionReportSummaryRecord {
    if (json == null) {
      throw new Error('PositionReportSummaryRecord.fromJS: parameter cannot be null');
    }
    return new PositionReportSummaryRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'PositionReportSummaryJson.id',
      ),
      positionTime: new Date(
        getFromOrThrow(
          json,
          (j) => propValueOrDefault(j, 'positionTime', '0001-01-01T00:00:00.00Z'),
          'PositionReportSummaryJson.positionTime',
        ),
      ),
      source: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'source', PositionReportSource.Pilot),
        'PositionReportSummaryJson.source',
      ),
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    positionTime: string | number | Date;
    source: PositionReportSource;
  }): [PositionReportSummaryRecord, Error[]] {
    if (json == null) {
      throw new Error('PositionReportSummaryRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new PositionReportSummaryRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'PositionReportSummaryJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      positionTime: tryOrDefault(
        () =>
          new Date(
            getFromOrThrow(
              json,
              (j) => propValueOrDefault(j, 'positionTime', '0001-01-01T00:00:00.00Z'),
              'PositionReportSummaryJson.positionTime',
            ),
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      source: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'source', PositionReportSource.Pilot),
            'PositionReportSummaryJson.source',
          ),
        (err) => errors.push(err),
      )!,
    });

    return [record, errors];
  }

  constructor(props: PositionReportSummaryProps) {
    super(props);
    if (props == null) {
      throw new Error('PositionReportSummaryRecord constructor parameter cannot be null');
    }
  }
}

export abstract class PositionReportSummaryViewModelBase implements PositionReportSummaryProps {
  protected readonly _props: PositionReportSummaryProps;
  private _json?: PositionReportSummaryProps;

  get id(): string {
    return this._props.id;
  }
  get positionTime(): Date {
    return this._props.positionTime;
  }
  get source(): PositionReportSource {
    return this._props.source;
  }

  public get isGpsAuto() {
    return this._props.source === PositionReportSource.GpsAuto;
  }
  public get isGpsAutoAdsb() {
    return this._props.source === PositionReportSource.GpsAutoAdsb;
  }
  public get isGpsManual() {
    return this._props.source === PositionReportSource.GpsManual;
  }
  public get isPilot() {
    return this._props.source === PositionReportSource.Pilot;
  }
  public get isTCS() {
    return this._props.source === PositionReportSource.TCS;
  }

  public isAnySource(...positionReportSource: PositionReportSource[]) {
    return positionReportSource.includes(this._props.source);
  }

  constructor(props: PositionReportSummaryProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        positionTime: this._props.positionTime,
        source: this._props.source,
      };
    }
    return this._json;
  }
}
